import { useEffect, useState } from "react";
import api, { formatErr, ROLE_LABELS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", password: "", role: "hotel_gm", hotel_id: null, region: "", department: "" };

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [meta, setMeta] = useState({ departments: [], regions: [] });
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/hotels").then((r) => setHotels(r.data));
    api.get("/meta").then((r) => setMeta(r.data));
  }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/users/${editing}`, form);
      else await api.post("/users", form);
      toast.success(editing ? "User updated" : "User created");
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("User removed");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const needsHotel = ["hotel_gm", "dept_head"].includes(form.role);
  const needsRegion = form.role === "regional_head";
  const needsDept = form.role === "dept_head";

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-stone-900">Users</h1>
          <p className="text-stone-500 mt-1 text-sm">Role-based access across the portfolio.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditing(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button" className="bg-[#14532D] hover:bg-[#166534]"><Plus className="w-4 h-4 mr-2" />Add User</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit User" : "Add User"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Name</Label>
                  <Input data-testid="user-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input data-testid="user-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Password {editing && <span className="text-stone-400">(leave blank to keep)</span>}</Label>
                  <Input data-testid="user-form-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(ROLE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {needsHotel && (
                <div className="space-y-1.5">
                  <Label>Hotel</Label>
                  <Select value={form.hotel_id || ""} onValueChange={(v) => setForm({ ...form, hotel_id: v })}>
                    <SelectTrigger data-testid="user-form-hotel"><SelectValue placeholder="Assign hotel" /></SelectTrigger>
                    <SelectContent>{hotels.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {needsRegion && (
                <div className="space-y-1.5">
                  <Label>Region</Label>
                  <Input data-testid="user-form-region" value={form.region || ""} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="e.g. West" />
                </div>
              )}
              {needsDept && (
                <div className="space-y-1.5">
                  <Label>Department</Label>
                  <Select value={form.department || ""} onValueChange={(v) => setForm({ ...form, department: v })}>
                    <SelectTrigger data-testid="user-form-department"><SelectValue placeholder="Choose department" /></SelectTrigger>
                    <SelectContent>{meta.departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <Button data-testid="user-form-save" onClick={save} disabled={!form.name || !form.email || (!editing && !form.password)} className="w-full bg-[#14532D] hover:bg-[#166534]">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[#E7E5DF] rounded-xl overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead className="bg-stone-50">
            <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-[#E7E5DF]">
              <th className="py-3 px-4">Name</th>
              <th className="py-3 px-4">Email</th>
              <th className="py-3 px-4">Role</th>
              <th className="py-3 px-4">Scope</th>
              <th className="py-3 px-4">MFA</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="users-table">
            {users.map((u) => {
              const hotel = hotels.find((h) => h.id === u.hotel_id);
              return (
                <tr key={u.id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/60">
                  <td className="py-3.5 px-4 text-sm font-medium text-stone-800">{u.name}</td>
                  <td className="py-3.5 px-4 text-sm text-stone-600">{u.email}</td>
                  <td className="py-3.5 px-4"><span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#D1FAE5] text-[#14532D]">{ROLE_LABELS[u.role]}</span></td>
                  <td className="py-3.5 px-4 text-sm text-stone-600">{hotel?.name || u.region || u.department || "All"}</td>
                  <td className="py-3.5 px-4">{u.mfa_enabled && <ShieldCheck className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />}</td>
                  <td className="py-3.5 px-4 text-right">
                    {user.role === "corporate_admin" && (
                      <>
                        <button data-testid={`user-edit-${u.id}`} className="p-1.5 rounded hover:bg-stone-100" onClick={() => { setEditing(u.id); setForm({ name: u.name, email: u.email, password: "", role: u.role, hotel_id: u.hotel_id, region: u.region || "", department: u.department || "" }); setOpen(true); }}>
                          <Pencil className="w-4 h-4 text-stone-500" strokeWidth={1.5} />
                        </button>
                        {u.id !== user.id && (
                          <button data-testid={`user-delete-${u.id}`} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" onClick={() => remove(u.id)}>
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
